import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const HealthReportViewer = () => {
  const { uniqueIdentifier } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isWhatsAppWebView, setIsWhatsAppWebView] = useState(false);

  // Detect if running in WhatsApp WebView
  const detectWhatsAppWebView = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isWhatsApp = userAgent.includes('whatsapp') || 
                      userAgent.includes('wv') && userAgent.includes('version') ||
                      window.navigator.standalone === false && /iphone|ipod|ipad/.test(userAgent);
    
    // Additional checks for in-app browsers
    const isInAppBrowser = userAgent.includes('wv') || // Android WebView
                          userAgent.includes('fban') || // Facebook
                          userAgent.includes('fbav') || // Facebook
                          userAgent.includes('instagram') ||
                          (window.navigator.standalone === false && /iphone|ipod|ipad/.test(userAgent));
    
    return isWhatsApp || isInAppBrowser;
  };

  useEffect(() => {
    if (!uniqueIdentifier) {
      setError('Invalid health report link');
      setLoading(false);
      return;
    }

    const isWebView = detectWhatsAppWebView();
    setIsWhatsAppWebView(isWebView);

    if (!isWebView) {
      // Normal browser - redirect immediately to API endpoint
      window.location.href = `https://api.caloriensweat.com/hreport/${uniqueIdentifier}`;
      return;
    }

    // WhatsApp WebView - show the page with instructions
    setLoading(false);
  }, [uniqueIdentifier]);

  const handleOpenInBrowser = (e) => {
    e.preventDefault();
    const apiUrl = `https://api.caloriensweat.com/hreport/${uniqueIdentifier}`;
    
    // Try multiple methods to open in external browser
    try {
      // Method 1: window.open with _blank
      const newWindow = window.open(apiUrl, '_blank', 'noopener,noreferrer');
      
      // Method 2: If window.open was blocked, fallback to location
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = apiUrl;
      }
    } catch (error) {
      // Method 3: Direct navigation as last resort
      window.location.href = apiUrl;
    }
  };

  const getDeviceInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipod|ipad/.test(userAgent);
    
    if (isIOS) {
      return {
        icon: '📱',
        title: 'iPhone/iPad Instructions:',
        steps: [
          'Tap the Share button (square with arrow)',
          'Select "Open in Safari"',
          'Your report will open in Safari browser'
        ]
      };
    } else {
      return {
        icon: '🤖',
        title: 'Android Instructions:',
        steps: [
          'Tap the menu (⋮) in the top right corner',
          'Select "Open in browser"',
          'Your report will open in Chrome or default browser'
        ]
      };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          fontSize: '48px',
          color: '#007bff'
        }}>
          📊
        </div>
        <div style={{
          fontSize: '18px',
          color: '#333',
          fontWeight: '500'
        }}>
          Opening your health report...
        </div>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #ddd',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  // WhatsApp WebView - show instructions page
  if (isWhatsAppWebView) {
    const instructions = getDeviceInstructions();
    
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          maxWidth: '400px',
          margin: '0 auto',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '30px 20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          {/* Header */}
          <div style={{
            fontSize: '48px',
            marginBottom: '20px'
          }}>
            📊
          </div>
          
          <h1 style={{
            fontSize: '24px',
            color: '#333',
            marginBottom: '15px',
            fontWeight: '600'
          }}>
            Health Report Ready
          </h1>
          
          <p style={{
            fontSize: '16px',
            color: '#666',
            marginBottom: '30px',
            lineHeight: '1.5'
          }}>
            This report may not open properly inside WhatsApp. Please open it in your browser for the best experience.
          </p>

          {/* Primary CTA Button */}
          <a
            href={`https://api.caloriensweat.com/hreport/${uniqueIdentifier}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenInBrowser}
            style={{
              display: 'inline-block',
              backgroundColor: '#007bff',
              color: 'white',
              padding: '15px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '30px',
              border: 'none',
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            📱 Open Report in Browser
          </a>

          {/* Instructions */}
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'left',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              <span style={{ marginRight: '10px', fontSize: '20px' }}>
                {instructions.icon}
              </span>
              {instructions.title}
            </div>
            
            <ol style={{
              margin: 0,
              paddingLeft: '20px',
              color: '#555'
            }}>
              {instructions.steps.map((step, index) => (
                <li key={index} style={{
                  marginBottom: '8px',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          {/* Footer */}
          <div style={{
            fontSize: '12px',
            color: '#888',
            lineHeight: '1.4'
          }}>
            Having trouble? Contact your coach for assistance.
          </div>
        </div>
      </div>
    );
  }

  // This should not be reached in normal flow, but keeping as fallback
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        textAlign: 'center',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '20px'
        }}>
          ⚠️
        </div>
        <h2 style={{
          color: '#dc3545',
          marginBottom: '15px',
          fontSize: '24px'
        }}>
          Report Not Available
        </h2>
        <p style={{
          color: '#666',
          marginBottom: '25px',
          lineHeight: '1.5'
        }}>
          Unable to load the health report. Please contact your coach for assistance.
        </p>
      </div>
    </div>
  );
};

export default HealthReportViewer;
